/**
 * ForRestaurants — the consolidated Restaurant Benefits hub.
 *
 * iter132 (Jul 2026) — Owner spec: collapse the previous 3-page
 * presentation ("Why NoFee" + "Restaurant Benefits" + "Pricing") into a
 * SINGLE page so a restaurant owner can understand the entire NoFee
 * value proposition without navigating away. The onboarding flow
 * itself is UNCHANGED — Create Account still hands off to the
 * approved wizard at /onboarding/account.
 *
 * Section order (top → bottom):
 *   1) Hero
 *   2) Why Choose NoFee — 5 quick pain-to-gain bullets
 *   3) Restaurant Benefits — 7 detailed benefit pillars
 *   4) ⚡ Fast by NoFee + On-time delivery card (existing tile)
 *   5) Savings Comparison — embedded interactive calculator +
 *      Marketplaces vs NoFee table (shared with /savings-calculator)
 *   6) Subscription Plans — Founding Partner ($299) + Enterprise
 *      (Coming Soon). Founding Partner CTA opens the existing
 *      onboarding at /onboarding/account.
 *   7) Bottom assurance strip
 */
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';
import SavingsCalculatorSection from '../components/SavingsCalculatorSection';
import {
  ArrowRight,
  Check,
  Sparkles,
  Zap,
  Crown,
  ShieldCheck,
  Clock,
  Percent,
  CreditCard,
  Smartphone,
  Bell,
  Users,
  DollarSign,
  Rocket,
  TrendingUp,
  PiggyBank,
} from 'lucide-react';

/* ── Why Choose NoFee — pain-to-gain quick hits ───────────────────── */
const WHY_BULLETS = [
  { Icon: Percent,     label: '0% marketplace commission',        detail: 'Flat $299/month replaces per-order commissions.' },
  { Icon: CreditCard,  label: 'Direct Stripe payouts',            detail: 'Payments land in your own Stripe account.' },
  { Icon: PiggyBank,   label: 'Lower operating costs',            detail: 'One predictable subscription — no surprise fees.' },
  { Icon: TrendingUp,  label: 'Keep more profit',                 detail: 'Every dollar saved goes back into your business.' },
  { Icon: DollarSign,  label: 'Faster cash flow',                 detail: 'No marketplace payout schedules holding your money.' },
];

/* ── Restaurant Benefits — detailed pillars ───────────────────────── */
const BENEFIT_PILLARS = [
  {
    Icon: Percent,
    title: '0% commission, forever',
    body: 'Flat $299/month replaces per-order commissions. The numbers move from a marketplace’s pocket into yours.',
    category: 'FINANCIAL',
  },
  {
    Icon: CreditCard,
    title: 'Direct Stripe payouts',
    body: 'Payments go directly to your own Stripe account, not a marketplace clearing account. NoFee never holds your money — you see every transaction.',
    category: 'FINANCIAL',
  },
  {
    Icon: Smartphone,
    title: 'QR & online ordering',
    body: 'Your own branded ordering page and at-table QR menus. Apple Pay, Google Pay — all included.',
    category: 'OPERATIONAL',
  },
  {
    Icon: Bell,
    title: 'Real-time SMS & email',
    body: 'Order notifications to your team and your customer the moment an order is placed. Built on Twilio + Resend.',
    category: 'OPERATIONAL',
  },
  {
    Icon: Users,
    title: 'You own the customer relationship',
    body: 'Your customers, your data, your email list. No marketplace standing between you and the people who pay for your food.',
    category: 'MARKETING',
  },
  {
    Icon: Zap,
    title: 'Fast by NoFee — verified delivery performance',
    body: 'Verified on-time deliveries confirmed by real customers. Fast Score is available inside your dashboard for operational monitoring — a real competitive advantage on the directory.',
    category: 'DASHBOARD',
  },
  {
    Icon: Crown,
    title: 'Built for modern restaurants',
    body: 'Multi-language menus (EN/PT/ES), kitchen tickets, printable receipts, NoFee Rewards loyalty — plus advanced restaurant management tools.',
    category: 'MANAGEMENT',
  },
];

const ForRestaurants = () => {
  return (
    <LanguageProvider>
      <div className="min-h-screen bg-black text-white">
        <SEO
          title="Restaurant Benefits — Why NoFee, Pricing & Savings Calculator"
          description="Everything a restaurant needs to know about NoFee in one page. 0% commission, direct Stripe payouts, savings calculator, delivery performance tools and simple $299/month pricing."
          path="/for-restaurants"
        />
        <Navbar />

        {/* ── 1) HERO — removed in iter133 (owner request).
            The label/title/paragraph/CTAs duplicated content that
            already lives inside the "Restaurant Benefits — Financial,
            operational, marketing, and dashboard tools" section right
            below. Page now begins directly with the benefit pillars. */}

        {/* ── 2) WHY CHOOSE NOFEE — removed in iter132 (owner request).
            The 5 bullets duplicated the "Restaurant Benefits" pillars
            that render right below. Keeping the section stripped out
            (no JSX) reduces vertical repetition and lets the visitor
            reach the detailed benefit pillars faster. */}

        {/* ── 3) RESTAURANT BENEFITS — 7 pillars ─────────────────── */}
        <section
          className="relative pt-32 sm:pt-36 lg:pt-44 pb-14 lg:pb-16"
          data-testid="section-restaurant-benefits"
        >
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
          <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
                Restaurant Benefits
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
                Financial, operational, marketing, and dashboard tools.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {BENEFIT_PILLARS.map(({ Icon, title, body, category }) => (
                <div
                  key={title}
                  data-testid={`benefit-pillar-${title.replace(/\s+/g, '-').toLowerCase().slice(0, 40)}`}
                  className="rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 hover:border-gold-500/40 transition-colors"
                >
                  <div className="flex items-center gap-2.5 mb-3">
                    <span className="w-9 h-9 rounded-xl bg-gold-500/10 border border-gold-500/25 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-gold-300" />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.18em] text-gold-400 font-bold">
                      {category}
                    </span>
                  </div>
                  <h3 className="text-lg font-black text-white leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-white/65 leading-relaxed">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4) KEY BENEFIT TILES — removed in iter133 (owner request).
            The "0% Commission Fees" and "Fast by NoFee" tiles duplicated
            the pillars already rendered in section 3 and the marketplace
            comparison below. Removing them lets the visitor flow
            directly from the benefit pillars into the savings math. */}

        {/* ── 5) SAVINGS COMPARISON — embedded calculator ────────── */}
        <section
          className="relative pt-4 pb-4"
          data-testid="section-savings-comparison"
        >
          <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center mb-8 lg:mb-10">
            <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
              Savings Comparison
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
              Traditional food-delivery platforms vs. NoFee.
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-[14.5px] text-white/55 leading-relaxed">
              Change three numbers below. The yearly savings figure is the difference that decides everything.
            </p>
          </div>
          <SavingsCalculatorSection primaryCtaTo="/onboarding/account" />
        </section>

        {/* ── 6) SUBSCRIPTION PLANS ──────────────────────────────── */}
        <section
          id="pricing"
          className="relative pt-4 pb-16 lg:pb-20 scroll-mt-24"
          data-testid="section-subscription-plans"
        >
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
            <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
              Subscription Plans
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
              Simple, transparent pricing.
            </h2>
            <p className="mt-3 max-w-xl mx-auto text-[14.5px] text-white/55">
              45 days free · Cancel anytime · No contracts.
            </p>
          </div>
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* PLAN 1 — Founding Partner ($299) */}
            <div
              className="group relative flex flex-col rounded-3xl border border-gold-500/30 bg-gradient-to-b from-zinc-950 to-black p-8 sm:p-10 hover:border-gold-500/60 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(212,175,55,0.40)]"
              data-testid="plan-founding-partner"
            >
              <div className="pointer-events-none absolute inset-0 rounded-3xl opacity-40 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.12),transparent_55%)]" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-gold-500/15 border border-gold-500/30 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-gold-300" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.24em] text-gold-400 font-semibold">
                    Founding Partner
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[9px] font-bold tracking-[0.16em] text-emerald-300 uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  $299<span className="text-lg font-bold text-white/45">/month</span>
                </h3>
                <p className="mt-2 text-[15px] text-white/70 max-w-md leading-relaxed">
                  Everything a restaurant needs to launch on NoFee — 0% commission, direct Stripe payouts, branded ordering, Fast by NoFee, and the full dashboard.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {[
                    '0% commission on all orders',
                    'Unlimited orders',
                    'Direct Stripe payouts',
                    'Branded ordering + QR menus',
                    'Fast by NoFee delivery ranking',
                    'Full restaurant dashboard',
                    '45-day free trial · cancel anytime',
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-[14px] text-white/80"
                    >
                      <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative mt-8 pt-6 border-t border-white/10">
                <Link
                  to="/onboarding/account"
                  data-testid="plan-founding-partner-cta"
                  className="inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
                >
                  <Rocket className="w-4 h-4" />
                  Create Account
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="text-xs text-white/45 text-center mt-2.5">
                  Continues straight into the existing onboarding.
                </p>
              </div>
            </div>

            {/* PLAN 2 — Enterprise (Coming Soon) */}
            <div
              className="group relative flex flex-col rounded-3xl border border-amber-500/20 bg-gradient-to-b from-zinc-950 to-black p-8 sm:p-10 hover:border-amber-500/40 transition-all duration-500 overflow-hidden"
              data-testid="plan-enterprise"
            >
              <div className="pointer-events-none absolute -top-12 -right-12 rotate-12 opacity-[0.06]">
                <div className="text-[130px] font-black tracking-tighter text-amber-300 leading-none select-none">
                  ENTERPRISE
                </div>
              </div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-amber-300" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.24em] text-amber-400 font-semibold">
                    Enterprise
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[9px] font-bold tracking-[0.16em] text-amber-300 uppercase">
                    <Clock className="w-2.5 h-2.5" />
                    Coming Soon
                  </span>
                </div>
                <h3 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                  Custom
                </h3>
                <p className="mt-2 text-[15px] text-white/70 max-w-md leading-relaxed">
                  Multi-location groups, restaurant associations, chambers of commerce, and franchise operators. Volume pricing, dedicated onboarding, and priority support.
                </p>
                <ul className="mt-6 space-y-2.5">
                  {[
                    'Multi-location management',
                    'Restaurant Association partnerships',
                    'Volume pricing & custom terms',
                    'Priority onboarding & support',
                    'Dedicated account manager',
                  ].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-[14px] text-white/75"
                    >
                      <Clock className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative mt-8 pt-6 border-t border-white/10">
                <button
                  type="button"
                  disabled
                  data-testid="plan-enterprise-cta"
                  className="inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-amber-500/40 bg-amber-500/[0.04] text-amber-200 font-bold cursor-not-allowed opacity-70"
                >
                  <Clock className="w-4 h-4" />
                  Coming Soon
                </button>
                <p className="text-xs text-white/45 text-center mt-2.5">
                  Join the Founding Partner plan today — upgrade paths will be available.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 7) BOTTOM ASSURANCE STRIP ──────────────────────────── */}
        <section className="relative pb-24 lg:pb-32">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-sm text-white/55">
              <div className="inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-gold-400" />
                <span>Secure payments by Stripe</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/10" />
              <div className="inline-flex items-center gap-2">
                <Check className="w-4 h-4 text-gold-400" />
                <span>0% commission · forever</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/10" />
              <div className="inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gold-400" />
                <span>US restaurant first · built for modern teams</span>
              </div>
            </div>
          </div>
        </section>

        <FooterSection />
      </div>
    </LanguageProvider>
  );
};

export default ForRestaurants;
